import type { MouseEvent } from 'react'
import { VerificationBadge } from '@/components/VerificationBadge'
import { formatRelativeTime } from '@/lib/time'
import type { BridgeInfo } from '@/lib/bridge'
import { verificationIdentifierForAuthor } from '@/lib/apBridgedIdentity'
import type { Post, PostAuthor } from '@/types/posts'

interface PostAuthorRowProps {
    author?: PostAuthor
    createdAt: string
    protocol?: Post['protocol']
    isActivityPub: boolean
    /** Bridge (Bridgy Fed) info for the author, or null when not bridged. */
    bridgeInfo: BridgeInfo | null | undefined
    /** Click on the display name (and the bridged @handle by default). Bridge-aware. */
    onAuthorClick: () => void
    /** Click on the native @handle. Defaults to {@link onAuthorClick}. */
    onNativeHandleClick?: () => void
    /** Click on the bridged @handle. Defaults to {@link onAuthorClick}. */
    onBridgedHandleClick?: () => void
    /** Disable the display-name button (no author-click target available). */
    authorClickDisabled?: boolean
    /** Text size for all text in the row. Defaults to 'sm'. */
    size?: 'sm' | 'xs'
    /** Whether to render the · separator and relative timestamp. Defaults to true. */
    showTimestamp?: boolean
    /**
     * 'inline': all items in one row (default).
     * 'stacked': display name + badge on line 1, handle on line 2, no timestamp.
     */
    layout?: 'inline' | 'stacked'
    /**
     * Verbatim text for the native @handle, replacing the default
     * `@{displayHandle ?? handle}`. For callers whose handle has its own
     * formatting rules (e.g. ActivityPub, where URL-shaped handles get no `@`).
     */
    nativeHandleText?: string
}

/**
 * The identity row shared by post surfaces: display name + verification badge +
 * (bridged @handle | native @handle) + separator + relative time.
 *
 * Presentational only — each caller passes its own bridge-aware click handlers so
 * navigation behavior is preserved. The handle is given a high flex-shrink so it
 * truncates before the display name, and the row never wraps.
 */
export function PostAuthorRow({
    author,
    createdAt,
    protocol,
    isActivityPub,
    bridgeInfo,
    onAuthorClick,
    onNativeHandleClick,
    onBridgedHandleClick,
    authorClickDisabled,
    size = 'sm',
    showTimestamp = true,
    layout = 'inline',
    nativeHandleText,
}: PostAuthorRowProps) {
    // Hide the native handle for Nostr posts that lack a clean display handle.
    const showNativeHandle = (protocol !== 'nostr' || !!author?.displayHandle) && !!author?.handle

    const stop = (handler: () => void) => (e: MouseEvent) => {
        e.stopPropagation()
        handler()
    }

    const textSizeClass = size === 'xs' ? 'text-xs' : 'text-sm'
    const handleClassName = `text-text-secondary-light dark:text-text-secondary-dark ${textSizeClass} truncate shrink-[9999] hover:underline focus:outline-none`
    const metaClassName = `text-text-secondary-light dark:text-text-secondary-dark ${textSizeClass} flex-shrink-0`
    const displayNameClassName = `font-semibold ${textSizeClass} text-text-primary-light dark:text-text-primary-dark truncate hover:underline focus:outline-none`

    const handleElement = bridgeInfo ? (
        <button
            type="button"
            onClick={stop(onBridgedHandleClick ?? onAuthorClick)}
            className={handleClassName}
            aria-label={`View ${bridgeInfo.isFromAP ? 'original' : 'bridged'} profile: ${bridgeInfo.bridgedHandle}`}
        >
            @{bridgeInfo.bridgedHandle}
        </button>
    ) : showNativeHandle ? (
        <button
            type="button"
            onClick={stop(onNativeHandleClick ?? onAuthorClick)}
            className={handleClassName}
        >
            {nativeHandleText ?? `@${author?.displayHandle ?? author?.handle}`}
        </button>
    ) : null

    if (layout === 'stacked') {
        return (
            <div className="min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                    <button
                        type="button"
                        onClick={stop(onAuthorClick)}
                        className={displayNameClassName}
                        disabled={authorClickDisabled}
                    >
                        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall back to handle, not render blank */}
                        {author?.displayName || author?.handle}
                    </button>
                    <VerificationBadge
                        nativeVerified={author?.isVerified}
                        authorDid={verificationIdentifierForAuthor(author)}
                        authorHandle={author?.handle}
                        authorDisplayName={author?.displayName}
                        isViaBridge={isActivityPub && !!bridgeInfo?.isFromAP}
                    />
                </div>
                {handleElement}
            </div>
        )
    }

    return (
        <div className="flex items-center gap-1 min-w-0">
            <button
                type="button"
                onClick={stop(onAuthorClick)}
                className={displayNameClassName}
                disabled={authorClickDisabled}
            >
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall back to handle, not render blank */}
                {author?.displayName || author?.handle}
            </button>
            <VerificationBadge
                nativeVerified={author?.isVerified}
                authorDid={verificationIdentifierForAuthor(author)}
                authorHandle={author?.handle}
                authorDisplayName={author?.displayName}
                isViaBridge={isActivityPub && !!bridgeInfo?.isFromAP}
            />
            {handleElement}
            {showTimestamp && (
                <>
                    <span className={metaClassName}>·</span>
                    <span className={metaClassName}>{formatRelativeTime(createdAt)}</span>
                </>
            )}
        </div>
    )
}
